'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import FadeInSection from '@/components/ui/FadeInSection';
import { StageMarker } from '@/components/layout/StoryRail';
import DragGallery from '@/components/ui/DragGallery';
import VideoBlock from '@/components/ui/VideoBlock';
import ImageComparisonSlider from '@/components/ui/ImageComparisonSlider';
import { CornerIcon, ShieldCheckIcon, DropletsIcon } from '@/components/ui/icons/benefitIcons';
import { tileImages, tileVideos, tileCompare } from '@/lib/media';

// Tile / bathrooms section. Title on top, then a two-column row: media (feature
// video + photo strip) on the left, lead copy + benefits on the right. On mobile
// the benefits become bordered cards; on desktop they read as a clean inline
// list. Each benefit highlights a key phrase via next-intl rich text (the <hl>
// tag in the messages).
export default function Services() {
  const t = useTranslations('tile');

  const hl = (chunks: ReactNode) => <span className="font-semibold text-primary">{chunks}</span>;

  const benefits = [
    { Icon: CornerIcon, title: t('benefit1Title'), desc: t.rich('benefit1Desc', { hl }) },
    { Icon: ShieldCheckIcon, title: t('benefit2Title'), desc: t.rich('benefit2Desc', { hl }) },
    { Icon: DropletsIcon, title: t('benefit3Title'), desc: t.rich('benefit3Desc', { hl }) },
  ];

  return (
    <section
      id="plytki"
      className="relative scroll-mt-2 border-t border-surface-bright/15 px-margin-mobile py-section-gap lg:scroll-mt-4 lg:px-margin-desktop"
    >
      <StageMarker n={1} />
      <div className="mx-auto w-full max-w-6xl">
        {/* Title — full width on top */}
        <FadeInSection direction="up">
          <p className="text-label-caps uppercase tracking-widest text-primary">{t('eyebrow')}</p>
          <h2 className="mt-stack-md max-w-4xl font-headline text-headline-xl font-bold text-on-surface">
            {t('headline')}
          </h2>
        </FadeInSection>

        {/* Two columns: media (left) + copy (right) */}
        <div className="mt-12 grid grid-cols-1 items-start gap-stack-lg lg:mt-16 lg:grid-cols-12 lg:gap-16">
          {/* Media */}
          <FadeInSection direction="right" distance={64} className="min-w-0 lg:col-span-7">
            <VideoBlock videos={tileVideos} label={t('eyebrow')} />
            <div className="mt-6">
              <DragGallery images={tileImages} label={t('galleryAlt')} />
            </div>
          </FadeInSection>

          {/* Copy */}
          <FadeInSection direction="left" distance={64} delay={0.1} className="min-w-0 lg:col-span-5">
            <p className="text-body-md text-on-surface-variant md:text-body-lg">{t('lead')}</p>

            <ul className="mt-stack-lg space-y-6">
              {benefits.map(({ Icon, title, desc }) => (
                <li
                  key={title}
                  className="relative overflow-hidden rounded-md border border-outline-variant/20 bg-surface-container-low p-6 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0"
                >
                  <div className="lg:flex lg:gap-stack-md">
                    <span className="block text-primary lg:mt-0.5 lg:shrink-0">
                      <Icon />
                    </span>
                    <div className="mt-stack-md min-w-0 lg:mt-0">
                      <h3 className="font-headline text-headline-md font-semibold text-on-surface">{title}</h3>
                      <p className="mt-stack-sm text-body-md text-on-surface-variant lg:mt-1">{desc}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </FadeInSection>
        </div>

        {/* Before / after — bespoke tile sinks & trays (custom work) */}
        <FadeInSection direction="up" delay={0.1} className="mt-16">
          <div className="forest-img-shadow relative mx-auto aspect-[3/4] w-full max-w-md overflow-hidden rounded-sm border border-outline-variant/30">
            <ImageComparisonSlider
              leftImage={tileCompare.before}
              rightImage={tileCompare.after}
              altLeft={t('compareAlt')}
              altRight={t('compareAlt')}
              autoSweep
            />
          </div>
        </FadeInSection>
      </div>
    </section>
  );
}
